import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Il riquadro che scivola sotto la voce attiva di un selettore.
 *
 * Misura la voce marcata `data-active="true"` dentro il contenitore e ne
 * restituisce posizione e dimensione, così un elemento assoluto può inseguirla
 * con una transizione. Vale per selettori a una riga (segmentato, schede) e a
 * due dimensioni (la griglia della Hub): si misura sempre, non si calcola per
 * percentuali — con `gap` e padding le percentuali cadono a fianco del bersaglio.
 *
 * `ready` esiste per NON animare la prima comparsa: senza, all'apertura il
 * riquadro partirebbe dall'angolo in alto a sinistra e volerebbe fino alla voce
 * attiva, dando l'impressione di un caricamento che non c'è.
 *
 * Rimisura anche quando il contenitore cambia forma (rotazione, comparsa della
 * tastiera, righe che si ritraggono): altrimenti dopo un cambio di layout il
 * riquadro resta dov'era e indica la voce sbagliata.
 */
export function useSlidingIndicator(value: string) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [rect, setRect] = useState<Rect | null>(null);
  const [ready, setReady] = useState(false);

  const measure = useCallback(() => {
    const box = containerRef.current;
    const active = box?.querySelector<HTMLElement>('[data-active="true"]');
    if (!box || !active) return;
    setRect({
      left: active.offsetLeft,
      top: active.offsetTop,
      width: active.offsetWidth,
      height: active.offsetHeight,
    });
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, value]);

  // La prima misura non deve animare; da lì in poi sì. Un frame di scarto basta
  // a far applicare la posizione iniziale prima che la transizione sia attiva.
  useEffect(() => {
    if (!rect || ready) return;
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, [rect, ready]);

  useEffect(() => {
    const box = containerRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    return () => ro.disconnect();
  }, [measure]);

  return { containerRef, rect, ready };
}

/**
 * Curva con sovraelongazione: il riquadro supera di poco il bersaglio e rientra.
 *
 * È il "rimbalzo" — dice che l'elemento si è MOSSO da qualche parte, mentre una
 * curva che si limita a rallentare fa sembrare che sia semplicemente apparso
 * altrove. Il quarto valore resta 1: la curva si chiude ferma, senza oscillare.
 */
export const BOUNCE = 'cubic-bezier(0.34, 1.42, 0.64, 1)';
